import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinance } from '@/contexts/FinanceContext';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { CounterpartyKind, FinanceCounterparty } from '@/types/finance';

const KIND_LABELS: Record<CounterpartyKind, string> = {
  supplier: 'Fornecedor',
  customer: 'Cliente',
  both: 'Fornecedor e Cliente',
};

const KIND_BADGE_CLASSES: Record<CounterpartyKind, string> = {
  supplier: 'bg-expense/10 text-expense border-expense/30',
  customer: 'bg-income/10 text-income border-income/30',
  both: 'bg-primary/10 text-primary border-primary/30',
};

interface ModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  edit?: FinanceCounterparty;
}

function CounterpartyModal({ open, onOpenChange, edit }: ModalProps) {
  const { addCounterparty, updateCounterparty } = useFinance();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CounterpartyKind>('both');
  const [document, setDocument] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (edit) {
      setName(edit.name);
      setKind(edit.kind);
      setDocument(edit.document ?? '');
      setEmail(edit.email ?? '');
      setPhone(edit.phone ?? '');
      setNotes(edit.notes ?? '');
    } else {
      setName('');
      setKind('both');
      setDocument('');
      setEmail('');
      setPhone('');
      setNotes('');
    }
  }, [open, edit]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (edit) {
      const updates: Partial<FinanceCounterparty> = { name: trimmed, kind };
      const docTrim = document.trim();
      const emailTrim = email.trim();
      const phoneTrim = phone.trim();
      const notesTrim = notes.trim();
      if (docTrim) updates.document = docTrim;
      if (emailTrim) updates.email = emailTrim;
      if (phoneTrim) updates.phone = phoneTrim;
      if (notesTrim) updates.notes = notesTrim;
      updateCounterparty(edit.id, updates);
    } else {
      await addCounterparty({
        name: trimmed,
        kind,
        ...(document.trim() && { document: document.trim() }),
        ...(email.trim() && { email: email.trim() }),
        ...(phone.trim() && { phone: phone.trim() }),
        ...(notes.trim() && { notes: notes.trim() }),
      });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-brand">
            {edit ? 'Editar Contraparte' : 'Nova Contraparte'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cp-name">Nome *</Label>
            <Input
              id="cp-name"
              placeholder="Ex: Karina Souza, Acme Suprimentos"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo *</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as CounterpartyKind)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="supplier">Fornecedor</SelectItem>
                <SelectItem value="customer">Cliente</SelectItem>
                <SelectItem value="both">Fornecedor e Cliente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cp-doc">CPF/CNPJ</Label>
              <Input
                id="cp-doc"
                placeholder="Opcional"
                value={document}
                onChange={(e) => setDocument(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cp-phone">Telefone</Label>
              <Input
                id="cp-phone"
                placeholder="Opcional"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-email">E-mail</Label>
            <Input
              id="cp-email"
              type="email"
              placeholder="Opcional"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-notes">Observações</Label>
            <Textarea
              id="cp-notes"
              placeholder="Opcional"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">{edit ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CounterpartiesPage() {
  const { clientId } = useCurrentClient();
  const { counterparties, deleteCounterparty, isLoading } = useFinance();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FinanceCounterparty | undefined>();
  const [confirmDelete, setConfirmDelete] = useState<FinanceCounterparty | undefined>();
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | CounterpartyKind>('all');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return counterparties.filter((cp) => {
      if (kindFilter !== 'all' && cp.kind !== kindFilter) return false;
      if (!term) return true;
      return (
        cp.name.toLowerCase().includes(term) ||
        cp.document?.toLowerCase().includes(term) ||
        cp.email?.toLowerCase().includes(term) ||
        cp.phone?.toLowerCase().includes(term)
      );
    });
  }, [counterparties, search, kindFilter]);

  if (!clientId) {
    return (
      <div className="rounded-xl border border-border bg-white p-8 text-center text-muted-foreground">
        <p className="text-sm">Selecione um cliente para gerenciar fornecedores e clientes.</p>
      </div>
    );
  }

  const openCreate = () => {
    setEditing(undefined);
    setModalOpen(true);
  };
  const openEdit = (cp: FinanceCounterparty) => {
    setEditing(cp);
    setModalOpen(true);
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Fornecedores e Clientes</h1>
          <p className="text-sm text-muted-foreground">
            {counterparties.length} cadastrado{counterparties.length === 1 ? '' : 's'}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Novo
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[260px] flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder="Buscar por nome, documento, contato…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as typeof kindFilter)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="supplier">Fornecedores</SelectItem>
            <SelectItem value="customer">Clientes</SelectItem>
            <SelectItem value="both">Ambos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-white p-12 text-center text-muted-foreground">
          <p className="font-medium">
            {search || kindFilter !== 'all'
              ? 'Nenhuma contraparte corresponde aos filtros'
              : 'Nenhum fornecedor ou cliente cadastrado'}
          </p>
          {!search && kindFilter === 'all' && (
            <p className="mt-1 text-sm">
              Cadastre fornecedores e clientes para filtrar relatórios e enriquecer transações.
            </p>
          )}
        </div>
      ) : (
        <div className="divide-y rounded-xl border border-border bg-white">
          {filtered.map((cp) => (
            <div
              key={cp.id}
              className="flex flex-wrap items-center justify-between gap-3 p-4 hover:bg-secondary/30"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{cp.name}</span>
                  <Badge variant="outline" className={`text-[10px] ${KIND_BADGE_CLASSES[cp.kind]}`}>
                    {KIND_LABELS[cp.kind]}
                  </Badge>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  {cp.document && <span>{cp.document}</span>}
                  {cp.email && (
                    <span className="inline-flex items-center gap-1">
                      <Mail size={11} />
                      {cp.email}
                    </span>
                  )}
                  {cp.phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone size={11} />
                      {cp.phone}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => openEdit(cp)}
                  title="Editar"
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => setConfirmDelete(cp)}
                  title="Excluir"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CounterpartyModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        {...(editing ? { edit: editing } : {})}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(undefined)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir contraparte?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete &&
                `"${confirmDelete.name}" será removido. Transações já associadas mantêm o histórico, mas o vínculo é desfeito.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDelete) deleteCounterparty(confirmDelete.id);
                setConfirmDelete(undefined);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
